/**
 * Resize image uploads and convert to WebP for smaller attachment files.
 * Non-image files are returned unchanged.
 */

const DEFAULT_MAX_EDGE = 1280;
const DEFAULT_WEBP_QUALITY = 0.8;

function baseNameWithoutExt(filename) {
  const name = String(filename || "image").trim() || "image";
  const idx = name.lastIndexOf(".");
  return idx > 0 ? name.slice(0, idx) : name;
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image."));
    };
    img.src = url;
  });
}

function canvasToWebpBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob !== "function") {
      reject(new Error("WebP export is not supported in this browser."));
      return;
    }
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not convert image to WebP."));
          return;
        }
        resolve(blob);
      },
      "image/webp",
      quality
    );
  });
}

/**
 * @param {File} file
 * @param {{ maxEdge?: number, quality?: number }} [options]
 * @returns {Promise<File>}
 */
export async function prepareAttachmentFile(file, options = {}) {
  if (!file || !(file instanceof File)) return file;
  if (!String(file.type || "").startsWith("image/")) return file;

  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = options.quality ?? DEFAULT_WEBP_QUALITY;

  try {
    const img = await loadImageElement(file);
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) return file;

    const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
    const width = Math.max(1, Math.round(srcW * scale));
    const height = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToWebpBlob(canvas, quality);
    const webpName = `${baseNameWithoutExt(file.name)}.webp`;
    return new File([blob], webpName, {
      type: "image/webp",
      lastModified: Date.now(),
    });
  } catch (_) {
    return file;
  }
}
